<template>
  <div class="purchase">
    <div class="inner">
      <div class="heading-purchase">
        <h2 id="heading-purchase">
          {{
            updating
              ? $t("purchaseLicense.headingUpdate")
              : $t("purchaseLicense.heading")
          }}
        </h2>
        <!-- <section v-if="updating"> -->
        <button
          v-if="updating"
          @click="showCancelSubscriptionModal = true"
          class="button cancel-button"
        >
          {{ $t("purchaseLicense.cancelSubscription") }}
        </button>
        <!-- </section> -->
      </div>

      <section>
        <div>
          <p class="vat-excluded">{{ $t("purchaseLicense.vatExcluded") }}</p>
          <div class="heading-license">
            <h3>{{ $t("purchaseLicense.selectLicense") }}</h3>

            <div class="button-license">
              <button class="button" @click="showComparePlansModal = true">
                {{ $t("purchaseLicense.comparePlans") }}
                <font-awesome-icon icon="chevron-right" class="icon" />
              </button>
            </div>
          </div>
        </div>
        <div class="modes">
          <div
            v-for="mode in modes"
            :key="mode.id"
            @click="
              selectMode(mode);
              summary();
            "
            class="box mode"
            :class="{ highlight: selectedMode.id === mode.id }"
            :id="`${mode.name.toLowerCase()}-button`"
          >
            <div>
              <div class="title">
                <h3>{{ mode.name }}</h3>
                <h4>
                  <span v-if="mode[selectedInterval].price">
                    {{ $t("purchaseLicense.from") }}
                  </span>
                  {{ mode[selectedInterval].formattedPrice }}
                </h4>
              </div>

              <p>{{ mode.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <section v-if="selectedMode.id">
        <h5>{{ $t("purchaseLicense.selectOneOrMoreDatabases") }}</h5>
        <div class="databases">
          <purchase-license-database-item
            v-for="database in databases"
            :key="database.id"
            @database-toggled="
              toggleDatabase(database);
              summary();
            "
            :disabled="hasSelectedTrial"
            :database="database"
            :selected-interval="selectedInterval"
            :is-selected="selectedDatabases.includes(database)"
          ></purchase-license-database-item>
        </div>
      </section>

      <form @submit.prevent="onSubmit">
        <section v-if="selectedMode.id">
          <h5>{{ $t("purchaseLicense.orderSummary") }}</h5>
          <div class="box order-summary">
            <div class="order-row">
              <span>{{ $t("purchaseLicense.subtotal") }}</span>
              <span>{{ subtotal }}</span>
            </div>
            <div class="order-row">
              <span>{{ $t("purchaseLicense.vat") }}</span>
              <span>{{ vat }}</span>
            </div>
            <div class="order-row">
              <strong>{{ $t("purchaseLicense.total") }}</strong>
              <strong
                >{{ total }}
                {{ $t(`purchaseLicense.interval.${selectedInterval}`) }}</strong
              >
            </div>
            <div class="order-row coupon-input" v-if="!updating">
              <input
                type="text"
                v-model="couponCode"
                :placeholder="$t('purchaseLicense.couponCodePlaceholder')"
                class="coupon-field"
                @keyup.enter="applyCoupon"
              />
              <button
                type="button"
                @click="applyCoupon"
                class="button apply-coupon-button"
                :disabled="!couponCode || validatingCoupon"
              >
                {{
                  validatingCoupon
                    ? $t("purchaseLicense.couponValidating")
                    : appliedCoupon
                    ? $t("purchaseLicense.couponApplied")
                    : $t("purchaseLicense.applyCoupon")
                }}
              </button>
            </div>
            <div v-if="couponError" class="coupon-error">
              {{ couponError }}
            </div>
            <div v-if="appliedCoupon" class="coupon-success">
              ✓
              {{
                $t("purchaseLicense.couponAppliedSuccessful", {
                  code: appliedCoupon.name || appliedCoupon.id
                })
              }}
            </div>
            <div class="order-row terms">
              <bisgraf-checkbox
                @input="acceptTermsOfUse = !acceptTermsOfUse"
                :value="acceptTermsOfUse"
              >
                <span
                  v-html="$t('generic.acceptTermsOfUse')"
                  class="contains-static-link"
                ></span>
              </bisgraf-checkbox>
            </div>
            <div class="order-row privacy-policy">
              <span
                v-html="$t('generic.privacyPolicy')"
                class="contains-static-link privacy-policy"
              ></span>
            </div>
            <div
              v-if="updating"
              class="update-note"
              v-html="$t('purchaseLicense.updateNote')"
            ></div>
          </div>
        </section>

        <section v-if="selectedMode.id">
          <div class="checkout-button">
            <bisgraf-input-with-button
              v-model="customerEmail"
              input-type="email"
              :input-placeholder="$t('generic.yourEmailAddress')"
              :input-disabled="updating"
              :button-text="checkoutButtonText"
              :button-id="checkoutButtonId"
              :button-disabled="!canCheckout"
            ></bisgraf-input-with-button>
          </div>
        </section>

        <div v-if="showErrorMessage">
          <message-modal
            :modal-is-open="showErrorMessage"
            :modal-title="$t('salesPitch.somethingWentWrongTitle')"
            :modal-text="$t('salesPitch.somethingWentWrongText')"
            @on-close="showErrorMessage = false"
          />
        </div>

        <div v-if="showCancelSubscriptionModal">
          <message-modal
            :modal-is-open="showCancelSubscriptionModal"
            :modal-title="$t('purchaseLicense.cancelSubscription')"
            :modal-text="$t('purchaseLicense.cancelSubscriptionText')"
            :button-text="$t('purchaseLicense.cancelSubscription')"
            @on-close="cancelSubscription"
          />
        </div>

        <div v-if="showCancelSuccess">
          <message-modal
            :modal-is-open="showCancelSuccess"
            :modal-title="$t('purchaseLicense.cancelSubscriptionSuccessTitle')"
            :modal-text="$t('purchaseLicense.cancelSubscriptionSuccessText')"
            @on-close="showCancelSuccess = false"
          />
        </div>
      </form>
      <compare-plans-modal
        :modal-is-open="showComparePlansModal"
        :modal-text="$t('salesPitch.somethingWentWrongText')"
        @on-close="showComparePlansModal = false"
      />
      <div class="hr"></div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { WebshopService } from "../services/WebshopService";
import { TrialService } from "../services/TrialService";
import BisgrafCheckbox from "../components/BisgrafCheckbox.vue";
import BisgrafInputWithButton from "./BisgrafInputWithButton.vue";
import PurchaseLicenseDatabaseItem from "./PurchaseLicenseDatabaseItem.vue";
import MessageModal from "./MessageModal.vue";
import ComparePlansModal from "./ComparePlansModal.vue";
import { BIZZDO, CREDITSAFE, MORNINGSTAR, TRIAL } from "../constants.ts";

export default {
  name: "PurchaseLicense",
  components: {
    BisgrafInputWithButton,
    BisgrafCheckbox,
    PurchaseLicenseDatabaseItem,
    MessageModal,
    ComparePlansModal
  },
  props: {
    selectedLicenseMode: String
  },
  data() {
    return {
      modes: [],
      databases: [],

      referenceCode: "",
      customerEmail: "",
      selectedInterval: "yearlyPrice",
      selectedMode: {},
      selectedDatabases: [],
      acceptTermsOfUse: false,
      existingUser: false,

      subtotal: "",
      vat: "",
      total: "",

      couponCode: "",
      appliedCoupon: null,
      couponError: "",
      validatingCoupon: false,

      updating: false,

      showErrorMessage: false,
      showComparePlansModal: false,
      showCancelSubscriptionModal: false,
      showCancelSuccess: false
    };
  },
  watch: {
    selectedLicenseMode() {
      if (this.selectedLicenseMode) {
        this.selectedMode = this.modes.find(
          mode =>
            mode.name.toLowerCase() === this.selectedLicenseMode.toLowerCase()
        );
        if (this.$i18n.locale === "en") {
          this.selectedDatabases = [
            this.databases.find(db => db.name.toLowerCase() === MORNINGSTAR)
          ];
        } else {
          this.selectedDatabases = [
            this.databases.find(db => db.name.toLowerCase() === CREDITSAFE),
            this.databases.find(db => db.name.toLowerCase() === BIZZDO)
          ];
        }

        this.summary();
        this.$emit("update:selectedLicenseMode", null);
      }
    }
  },
  computed: {
    canCheckout() {
      if (this.updating) {
        const modeChanged =
          this.selectedMode.id !== this.customerSubscription.mode.id;

        const selectedDatabaseIds = this.selectedDatabases.map(
          database => database.id
        );
        const customerSubscriptionDatabaseIds = this.customerSubscription.databases.map(
          database => database.id
        );
        const difference = selectedDatabaseIds
          .filter(x => !customerSubscriptionDatabaseIds.includes(x))
          .concat(
            customerSubscriptionDatabaseIds.filter(
              x => !selectedDatabaseIds.includes(x)
            )
          );

        const databasesChanged = difference.length !== 0;
        const intervalChanged =
          this.selectedInterval !== this.customerSubscription.interval;
        if (!modeChanged && !databasesChanged && !intervalChanged) {
          return false;
        }
      }

      return (
        this.selectedMode.id &&
        this.selectedDatabases.length > 0 &&
        this.acceptTermsOfUse
      );
    },
    hasSelectedTrial() {
      return this.selectedMode.name.toLowerCase() === TRIAL;
    },
    checkoutButtonText() {
      if (this.updating) {
        return this.$t("purchaseLicense.updateSubscription");
      } else if (this.hasSelectedTrial) {
        return this.$t("purchaseLicense.registerForFree");
      } else {
        return this.$t("purchaseLicense.proceedToCheckout");
      }
    },
    checkoutButtonId() {
      if (this.updating) {
        return "updateSubscriptionButton";
      } else if (this.hasSelectedTrial) {
        return "trialButton";
      } else {
        return "checkoutButton";
      }
    }
  },
  async created() {
    const queryParams = new URL(window.location).searchParams;
    const stripeSubscriptionId = queryParams.get("stripeSubscriptionId");
    if (stripeSubscriptionId) {
      await this.getCustomerSubscription(stripeSubscriptionId);
      this.$currency = this.customerSubscription.currency;
    }

    const existingUser = queryParams.get("existingUser");
    if (existingUser) {
      this.existingUser = existingUser;
    }

    const email = queryParams.get("email");
    if (email) {
      this.customerEmail = email;
    }

    await this.getSubscriptions();

    this.selectDefaultDatabases();

    if (this.customerSubscription) {
      this.setSubscriptionData();
      this.summary();
    }
  },
  methods: {
    async getSubscriptions() {
      const subscriptions = await WebshopService.getSubscriptions(
        this.$i18n.locale,
        this.$currency
      );
      this.modes = subscriptions.modes.sort(
        (a, b) =>
          a[this.selectedInterval].price - b[this.selectedInterval].price
      );
      this.databases = subscriptions.databases;
    },

    selectDefaultDatabases() {
      this.databases.forEach(database => {
        if (database.defaultSelected) {
          this.selectedDatabases.push(database);
        }
      });
    },

    async getCustomerSubscription(stripeSubscriptionId) {
      this.customerSubscription = await WebshopService.getCustomerSubscription(
        stripeSubscriptionId
      );
    },

    setSubscriptionData() {
      this.updating = true;
      this.customerEmail = this.customerSubscription.email;
      this.selectedInterval = this.customerSubscription.interval;
      this.selectedMode = this.modes.find(
        mode => mode.id === this.customerSubscription.mode?.id
      );
      this.selectedDatabases = this.databases.filter(databaseA =>
        this.customerSubscription.databases.find(
          databaseB => databaseB.id === databaseA.id
        )
      );
      this.modes = this.modes.filter(mode => mode.name.toLowerCase() !== TRIAL);
    },

    async summary() {
      if (!this.selectedMode.id) {
        return;
      }
      const items = [this.selectedMode, ...this.selectedDatabases].map(
        product => product[this.selectedInterval].price
      );

      const orderSummary = await WebshopService.calculateOrderSummaryForItems(
        items,
        this.$currency
      );
      this.subtotal = orderSummary.subtotal;
      this.vat = orderSummary.vat;
      this.total = orderSummary.total;
    },

    async onSubmit() {
      this.showErrorMessage = false;
      if (this.updating) {
        this.updateSubscription();
      } else if (this.hasSelectedTrial) {
        this.signupForTrial();
      } else {
        this.checkout();
      }
    },

    async updateSubscription() {
      try {
        const response = await WebshopService.updateSubscription(
          this.customerSubscription.id,
          this.customerEmail,
          this.getCheckoutItems()
        );

        if (response.error === "Payment failed") {
          window.location.href = `${window.location.origin}/update-failure`;
        } else {
          window.location.href = `${window.location.origin}/update-success`;
        }
      } catch (error) {
        this.showErrorMessage = true;
      }
    },

    async signupForTrial() {
      try {
        await TrialService.trialUserSignup(
          this.customerEmail,
          this.$i18n.locale
        );
        window.location.href = `${window.location.origin}/success`;
      } catch (error) {
        this.showErrorMessage = true;
      }
    },

    async cancelSubscription() {
      this.showCancelSubscriptionModal = false;
      try {
        const response = await WebshopService.cancelSubscription(
          this.customerSubscription.id
        );

        this.showCancelSuccess = true;
      } catch (error) {
        this.showErrorMessage = true;
      }
    },

    async checkout() {
      const { publishableKey } = await WebshopService.getStripeConfiguration();
      const { sessionId } = await this.createCheckoutSession();

      if (sessionId === undefined) {
        return;
      }

      const stripe = await loadStripe(publishableKey);
      stripe.redirectToCheckout({
        sessionId
      });
    },

    async createCheckoutSession() {
      try {
        const res = await WebshopService.createCheckoutSession(
          this.$i18n.locale,
          this.customerEmail,
          this.existingUser,
          this.getCheckoutItems(),
          this.appliedCoupon ? this.couponCode : undefined
        );
        return res;
      } catch (error) {
        this.showErrorMessage = true;
        return {};
      }
    },

    async applyCoupon() {
      if (!this.couponCode) {
        return;
      }

      this.validatingCoupon = true;
      this.couponError = "";
      this.appliedCoupon = null;

      try {
        const result = await WebshopService.validateCoupon(this.couponCode);

        if (result.valid) {
          this.appliedCoupon = result.coupon;
          this.couponError = "";
        } else {
          console.warn("Invalid coupon:", result.message);
          this.couponError = this.$t("purchaseLicense.invalidCoupon");
          this.appliedCoupon = null;
        }
      } catch (error) {
        this.couponError = this.$t("purchaseLicense.errorValidatingCoupon");
        console.error("Error validating coupon:", error);
        this.appliedCoupon = null;
      } finally {
        this.validatingCoupon = false;
      }
    },

    formatAmount(amount, currency) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency?.toUpperCase() || "USD"
      }).format(amount / 100);
    },

    getCheckoutItems() {
      const alphabeticallySortedDatabases = this.getSortedDatabases();
      return [
        this.selectedMode,
        ...alphabeticallySortedDatabases
      ].map(product => ({ priceId: product[this.selectedInterval].id }));
    },

    getSortedDatabases() {
      return this.selectedDatabases.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },

    selectMode(mode) {
      if (mode.name.toLowerCase() === TRIAL && this.$i18n.locale === "en") {
        this.selectedDatabases = [
          this.databases.find(db => db.name.toLowerCase() === MORNINGSTAR)
        ];
      } else if (mode.name.toLowerCase() === TRIAL) {
        this.selectedDatabases = [
          this.databases.find(db => db.name.toLowerCase() === CREDITSAFE),
          this.databases.find(db => db.name.toLowerCase() === BIZZDO)
        ];
      }
      this.selectedMode = mode;
    },

    toggleDatabase(database) {
      const databaseAlreadySelected = this.selectedDatabases.some(
        d => d.id === database.id
      );
      if (databaseAlreadySelected) {
        this.selectedDatabases = this.selectedDatabases.filter(
          d => d.id !== database.id
        );
      } else {
        this.selectedDatabases.push(database);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/variables.scss";
@import "@/mixins.scss";

.purchase {
  position: relative;
  padding-top: 50px;
  background-color: $color-background;
}

.inner {
  @include max-width-container;
  overflow: auto;
}

.heading-purchase {
  display: flex;
  justify-content: space-between;
  align-items: start;

  @media only screen and (max-width: $breakpoint-large) {
    flex-direction: column;
    justify-content: center;
  }

  h2 {
    font-size: 42px;
    margin-top: 0;
  }
}

.heading-subscription {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media only screen and (max-width: $breakpoint-large) {
    flex-direction: column;
    justify-content: center;
    margin: -20px 0 50px 0;
  }

  h3 {
    font-size: 28px;
  }
}

.vat-excluded {
  margin-bottom: 20px;
}

.heading-license {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;

  @media only screen and (max-width: $breakpoint-large) {
    flex-direction: column;
    justify-content: center;
    margin: -20px 0 50px 0;
  }

  h3 {
    margin: 0;
    font-size: 28px;
  }

  .button-license {
    padding: 1rem;

    .button {
      box-shadow: 0px 0px 20px 1px $color-box-shadow;
      // changed css for iframe background: $color-secondary;
      background: #337078;
      padding: 10px 20px;

      .icon {
        margin-left: 16px;
      }
    }
  }
}

.modes {
  display: flex;
  justify-content: space-between;
  margin-bottom: 50px;

  @media only screen and (max-width: $breakpoint-large) {
    align-items: center;
    flex-direction: column;
    justify-content: center;
  }

  .mode {
    width: 31.5%;
    box-sizing: border-box;
    align-items: center;
    padding: 25px 30px 12px;
    transition: none;
    cursor: pointer;

    &:hover {
      //changed css for iframe background-color: #fafafa;
      background-color: #429eaa;
    }

    @media only screen and (max-width: $breakpoint-large) {
      width: 100%;
      margin-bottom: 30px;
      max-width: 600px;
    }

    .title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      h3 {
        margin: 0;
        font-size: 24px;
        letter-spacing: 0.5px;
      }

      h4 {
        margin: 0;
        font-weight: 600;
        font-size: 19px;

        span {
          font-size: 13px;
          margin-right: 1px;
          font-weight: normal;
        }
      }
    }

    p {
      font-size: 16px;
      line-height: 1.5em;

      &:first-of-type {
        margin-top: 0;
      }
    }
  }
}

h5 {
  margin: 0 0 20px 0;
  font-size: 22px;
  letter-spacing: 0.5px;

  @media only screen and (max-width: $breakpoint-large) {
    text-align: center;
    margin-bottom: 30px;
  }
}

.databases {
  display: flex;
  margin-bottom: 50px;
  flex-wrap: wrap;
  justify-content: space-between;
  flex-direction: column;
}

.my-numbers {
  display: flex;
  padding: 0 25px 20px;
  margin-bottom: 50px;
  flex-wrap: wrap;

  iframe {
    flex: 0 1 auto;
    width: 100%;
    height: 337px;
    border: none;
  }

  .contains-static-link {
    text-align: center;
    flex: 1 1 auto;

    a {
      cursor: pointer;
    }
  }
}

.mode,
.interval {
  &.highlight {
    color: white;
    //changed css for iframe added ->
    background: #337078;
    //changed css for iframe added ->
    background-color: #20575d;
  }
}

.order-summary {
  padding: 20px 25px;

  .order-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 18px;
  }

  .coupon-input {
    margin-top: 16px;
    gap: 20px;
    padding-right: 16px;
    align-items: center;

    .coupon-field {
      flex: 1;
      padding: 8px;
      border: 1px solid #ccc;
      border-radius: 4px;
      font-size: 15px;
      &::placeholder {
        font-size: 15px;
      }
    }

    .apply-coupon-button {
      height: 37px;
      padding-block: 0;
      border: none;
      background-color: #337078;
      border-radius: 4px;
      font-size: 14px;
      white-space: nowrap;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        opacity: 0.9;
      }
    }
  }

  .coupon-error {
    color: #d9534f;
    font-size: 15px;
    margin-top: 5px;
  }

  .coupon-success {
    color: #337078;
    font-size: 16px;
    margin-top: 5px;
    font-weight: 500;
  }

  .terms,
  .update-note {
    margin-top: 20px;
    font-size: 16px;
  }

  .privacy-policy {
    font-size: 16px;
  }
}

.checkout-button {
  margin-top: 1rem;
}

.cancel-button {
  margin: 0;
  border: none;
  background: $color-secondary;
  float: right;
}

.hr {
  // changed css for margin: 130px auto (30px - 9/2);
  margin: 40px auto (30px - 9/2);

  // changed css for background: $color-primary;
  border-radius: 9px;
  width: 50%;
  height: 9px;
}

@media only screen and (max-width: 1000px) {
  .modes,
  .databases {
    grid-template-columns: 1fr;
  }
}
</style>
